import React from 'react';
import { PaginatedUsers } from '../../types/profile';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface FollowersListProps {
    users: PaginatedUsers;
    type: 'followers' | 'following';
    onLoadMore?: () => void;
    loading?: boolean;
}

const FollowersList: React.FC<FollowersListProps> = ({
    users,
    type,
    onLoadMore,
    loading = false,
}) => {
    const { t } = useTranslation('profile');

    const hasMore = users.page < users.totalPages;
    const title =
        type === 'followers'
            ? t('followers.title', 'Followers')
            : t('following.title', 'Following');

    return (
        <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                <span className="text-sm text-gray-500">
                    {users.total} {t('users.total', 'total')}
                </span>
            </div>

            {users.items.length === 0 ? (
                <div className="text-center py-8">
                    <svg
                        className="mx-auto h-12 w-12 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                    </svg>
                    <p className="mt-2 text-sm text-gray-500">
                        {type === 'followers'
                            ? t('followers.empty', 'No followers yet')
                            : t('following.empty', 'Not following anyone yet')}
                    </p>
                </div>
            ) : (
                <>
                    <div className="space-y-3">
                        {users.items.map((user) => (
                            <Link
                                key={user.id}
                                to={`/profile/${user.id}`}
                                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {/* Avatar */}
                                {user.avatarUrl ? (
                                    <img
                                        src={user.avatarUrl}
                                        alt={user.fullName || user.username}
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                                        {(user.fullName || user.username).charAt(0).toUpperCase()}
                                    </div>
                                )}

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">
                                        {user.fullName || user.username}
                                    </p>
                                    <p className="text-sm text-gray-500 truncate">
                                        @{user.username}
                                    </p>
                                </div>

                                {/* Arrow */}
                                <svg
                                    className="w-5 h-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </Link>
                        ))}
                    </div>

                    {/* Load More */}
                    {hasMore && (
                        <div className="text-center mt-4">
                            <button
                                onClick={onLoadMore}
                                disabled={loading}
                                className="px-4 py-2 text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
                            >
                                {loading
                                    ? t('common:loading', 'Loading...')
                                    : t('common:loadMore', 'Load More')}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default FollowersList;
